import React, { useState, useEffect } from 'react';
import './Login.css';
import packageJson from '../package.json';

import { getApiBaseUrl } from './utils/apiConfig';
import { clearAuthSession, fetchSessionUser, hasAuthSession, markAuthSession } from './utils/authSession';

const API_BASE_URL = getApiBaseUrl();

const Login = ({ onLoginSuccess }) => {
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Verificar se ja existe sessao valida (cookie HttpOnly) ao carregar
  useEffect(() => {
    const checkExistingToken = async () => {
      if (!hasAuthSession()) return;
      try {
        const response = await fetch(`${API_BASE_URL}/api/auth/verify`, {
          method: 'GET',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        });

        const result = await response.json();

        if (result.success) {
          markAuthSession();
          localStorage.setItem('userData', JSON.stringify(result.user));
          onLoginSuccess(result.user);
          return;
        }

        const refreshRes = await fetch(`${API_BASE_URL}/api/auth/refresh`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        });
        const refreshData = await refreshRes.json();
        if (refreshData.success) {
          markAuthSession();
          const verifyRes = await fetch(`${API_BASE_URL}/api/auth/verify`, {
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
          });
          const verifyData = await verifyRes.json();
          if (verifyData.success) {
            localStorage.setItem('userData', JSON.stringify(verifyData.user));
            onLoginSuccess(verifyData.user);
            return;
          }
        }
        clearAuthSession();
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('userData');
      } catch (error) {
        console.error('Erro ao verificar token:', error);
        clearAuthSession();
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('userData');
      }
    };

    checkExistingToken();
  }, [onLoginSuccess]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Limpar erro quando o usuário digita
    if (error) {
      setError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.username.trim() || !formData.password.trim()) {
      setError('Por favor, preencha todos os campos');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          username: formData.username.trim(),
          password: formData.password
        })
      });

      const result = await response.json();


      if (result.success) {
        markAuthSession();
        const sessionUser = await fetchSessionUser(API_BASE_URL);
        if (!sessionUser) {
          clearAuthSession();
          setError('Sessão inválida após login. Tente novamente.');
          return;
        }
        localStorage.setItem('userData', JSON.stringify(sessionUser));
        onLoginSuccess(sessionUser);
      } else {
        setError(result.message || 'Erro no login');
      }
    } catch (error) {
      console.error('Erro no login:', error);
      setError('Erro de conexão. Verifique se o servidor está rodando.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSubmit(e);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <div className="login-logo">
            <i className="bi bi-gear-fill"></i>
          </div>
          <h1>Verve</h1>
          <p>Sistema de Gestão de Ordens de Serviço</p>
        </div>

        <form className="login-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="username">Usuário</label>
            <div className="input-container">
              <i className="bi bi-person input-icon"></i>
              <input
                type="text"
                id="username"
                name="username"
                value={formData.username}
                onChange={handleInputChange}
                onKeyPress={handleKeyPress}
                placeholder="Digite seu usuário"
                className="form-input"
                autoComplete="username"
                autoFocus
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="password">Senha</label>
            <div className="input-container">
              <i className="bi bi-lock input-icon"></i>
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                onKeyPress={handleKeyPress}
                placeholder="Digite sua senha"
                className="form-input"
                autoComplete="current-password"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex="-1"
              >
                <i className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'}`}></i>
              </button>
            </div>
          </div>

          {error && (
            <div className="error-message">
              <i className="bi bi-exclamation-circle"></i>
              {error}
            </div>
          )}

          <button
            type="submit"
            className="login-button"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <i className="bi bi-arrow-repeat spin"></i>
                Entrando...
              </>
            ) : (
              <>
                <i className="bi bi-box-arrow-in-right"></i>
                Entrar
              </>
            )}
          </button>
        </form>

        <div className="login-footer">
          <p>© 2026 Verve - versão {packageJson.version}</p>
        </div>
      </div>
    </div>
  );
};

export default Login; 